import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Hammer from "hammerjs";

class BodySwipable extends React.Component {

  static propTypes = {
    className: PropTypes.string,
    scrollableSelector: PropTypes.string.isRequired,
    scrollableRef: PropTypes.any,
    children: PropTypes.any.isRequired,
    isMobile: PropTypes.bool,
  };

  static defaultProps = {
    className: "",
  };

  constructor(props) {
    super(props);

    this.wrapperRef = React.createRef();
    this.scrollableElement = null;

    this.hammer = null;
    this.startScrollLeft = 0;
    this.state = { swipeActive: false };
  }

  componentDidMount() {
    this.scrollableElement = this.getScrollableElement();
    this.initHammer();
  }

  componentWillUnmount() {
    this.destroyHammer();
  }

  componentDidUpdate(prevProps) {
    // If selector/ref changes (rare), re-resolve + re-init.
    if (
      prevProps.scrollableSelector !== this.props.scrollableSelector ||
      prevProps.scrollableRef !== this.props.scrollableRef ||
      prevProps.isMobile !== this.props.isMobile
    ) {
      this.scrollableElement = this.getScrollableElement();
      this.destroyHammer();
      this.initHammer();
    }
  }

  getScrollableElement = () => {
    return this.props.scrollableRef?.current
      ? this.props.scrollableRef.current
      : document.querySelector(this.props.scrollableSelector);
  };

  canScrollHorizontally = () => {
    const el = this.scrollableElement;
    if (!el) return false;
    return el.scrollWidth > el.clientWidth;
  };

  initHammer = () => {
    if (this.props.isMobile) return; // on mobile native scroll is best
    const node = this.wrapperRef.current;
    if (!node) return;

    // Create manager
    this.hammer = new Hammer.Manager(node);

    // Pan recognizer: horizontal only, with a small threshold
    const pan = new Hammer.Pan({
      direction: Hammer.DIRECTION_HORIZONTAL,
      threshold: 5, // tune 5–10
    });

    this.hammer.add(pan);

    // Optional: stop text selection / native drag while panning
    node.style.userSelect = "none";
    node.style.webkitUserSelect = "none";
    node.style.msUserSelect = "none";

    this.hammer.on("panstart", this.onPanStart);
    this.hammer.on("panmove", this.onPanMove);
    this.hammer.on("panend pancancel", this.onPanEnd);
  };

  destroyHammer = () => {
    if (this.hammer) {
      this.hammer.off("panstart", this.onPanStart);
      this.hammer.off("panmove", this.onPanMove);
      this.hammer.off("panend pancancel", this.onPanEnd);
      this.hammer.destroy();
      this.hammer = null;
    }
  };

  onPanStart = (ev) => {
    if (!this.scrollableElement) this.scrollableElement = this.getScrollableElement();
    if (!this.scrollableElement || !this.canScrollHorizontally()) return;

    // If an auto-center animation is running, cancel it before we take over scrollLeft
    if (typeof this.scrollableElement._cancelAutoScroll === "function") {
      this.scrollableElement._cancelAutoScroll();
      this.scrollableElement._cancelAutoScroll = null;
    }

    this.startScrollLeft = this.scrollableElement.scrollLeft;
    this.setState({ swipeActive: true });

    // Prevent image dragging / text selection side effects
    // (Hammer uses touch-action sometimes; this helps for mouse)
    if (ev.srcEvent && ev.srcEvent.cancelable) ev.srcEvent.preventDefault();
  };

  onPanMove = (ev) => {
    if (!this.scrollableElement || !this.state.swipeActive) return;

    // Hammer's deltaX is total movement since panstart.
    // Dragging right should scroll left (natural “grab” behavior):
    this.scrollableElement.scrollLeft = this.startScrollLeft - ev.deltaX;

    if (ev.srcEvent && ev.srcEvent.cancelable) ev.srcEvent.preventDefault();
  };

  onPanEnd = () => {
    this.setState({ swipeActive: false });
    this.startScrollLeft = 0;
  };

  render() {
    if (this.props.isMobile) {
      return <div className={this.props.className}>{this.props.children}</div>;
    }

    const cls =
      this.props.className +
      " swipable" +
      (this.state.swipeActive ? " swipable--active" : "");

    return (
      <div
        ref={this.wrapperRef}
        className={cls}
        // Important when mixing with touch devices/laptops:
        style={{ touchAction: "pan-y" }}
      >
        {this.props.children}
      </div>
    );
  }
}

const mapStateToProps = (store) => ({
  isMobile: store.serverState.isMobile,
});

export default connect(mapStateToProps)(BodySwipable);
